import * as React from 'react';

const DIACRITICS = /\p{Diacritic}/gu;

/**
 * Case- and accent-insensitive form of a string, so a query for "e" matches "é".
 *
 * react-aria compared through an `Intl.Collator` on every keystroke against every option. Folding
 * both sides once — the query per keystroke, an option's text once per collection — leaves the match
 * itself a plain `String.includes`, which is what makes a list of thousands typable.
 */
export function foldForSearch(text: string): string {
  return text.normalize('NFD').replace(DIACRITICS, '').toLowerCase();
}

/**
 * A row carrying its own folded text. `ComboBoxRow` satisfies this; anything else gets folded on
 * the spot.
 */
export interface SearchableRow {
  haystack: string;
}

function haystackOf(row: unknown): string {
  if (typeof row === 'object' && row !== null && 'haystack' in row) {
    const { haystack } = row;
    if (typeof haystack === 'string') return haystack;
  }
  return foldForSearch(String(row));
}

export type ComboBoxFilter = (row: unknown, query: string) => boolean;

/**
 * Matches rows against the raw query Base UI hands its `filter` prop, folding that query once per
 * keystroke rather than once per row.
 */
export function createComboBoxFilter(): ComboBoxFilter {
  let lastQuery: string | undefined;
  let folded = '';

  return (row, query) => {
    if (query !== lastQuery) {
      lastQuery = query;
      folded = foldForSearch(query.trim());
    }
    return folded ? haystackOf(row).includes(folded) : true;
  };
}

export function useComboBoxFilter(): ComboBoxFilter {
  return React.useMemo(createComboBoxFilter, []);
}
